import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import HeroSection from '../components/shared/HeroSection';
import SectionTitle from '../components/shared/SectionTitle';
import CTASection from '../components/shared/CTASection';
import SEOHead from '../components/shared/SEOHead';
import { ParallaxSection, ParallaxElement, FadeInOnScroll } from '../components/shared/Parallax';
import { locationSEO, generateBreadcrumbSchema, generateServiceSchema } from '../utils/seo';
import { MapPin, Check, Phone, ArrowRight } from 'lucide-react';
import { companyInfo } from '../data/mock';

const LocationPage = () => {
  const { locationId } = useParams();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [locationId]);

  // Map URL slugs to location keys
  const locationMap = {
    'pisa': 'pisa',
    'provincia-pisa': 'provinciaPisa',
    'lucca': 'lucca',
    'livorno': 'livorno',
    'firenze': 'firenze',
    'massa-carrara': 'massaCarrara',
    'pistoia': 'pistoia'
  };

  const locationKey = locationMap[locationId];
  const location = locationSEO[locationKey];

  if (!location) {
    return <Navigate to="/aree-servite" replace />;
  }

  // Città che mostrano solo bioclimatiche, pergotende e vetrate (NO tende da sole)
  const bioclimaticheOnlyCities = ['lucca', 'livorno', 'firenze', 'massaCarrara', 'pistoia'];
  const isBioclimaticheOnly = bioclimaticheOnlyCities.includes(locationKey);

  // Servizi completi (per Pisa e provincia)
  const allServices = [
    {
      name: "Pergole Bioclimatiche",
      description: `Pergole bioclimatiche con lamelle orientabili a ${location.content?.areas?.[0] || 'nella zona'}. Strutture in alluminio certificate, controllo domotico.`,
      link: "/bioclimatiche",
      features: ["Lamelle orientabili 0°-150°", "Alluminio certificato", "Sensori climatici", "App smartphone"]
    },
    {
      name: "Pergotende",
      description: "Pergotende su misura per terrazzi e giardini. Protezione da sole, pioggia e vento tutto l'anno.",
      link: "/pergotende",
      features: ["Teli impermeabili", "Strutture autoportanti", "Design personalizzato", "Motorizzazione opzionale"]
    },
    {
      name: "Tende da Sole",
      description: "Tende da sole per esterni: a bracci, a caduta, cappottine. Tessuti premium con protezione UV.",
      link: "/tende-da-sole",
      features: ["Tessuti certificati UV", "Motorizzazione smart", "Sensori sole/vento", "Ampia scelta colori"]
    },
    {
      name: "Vetrate Panoramiche",
      description: "Chiusure vetrate scorrevoli per pergole e verande. Vetri temperati, isolamento termico.",
      link: "/vetrate-panoramiche",
      features: ["Vetri temperati", "Sistemi scorrevoli", "Isolamento termico", "Apertura totale"]
    }
  ];

  // Servizi ridotti per le altre città (solo bioclimatiche, pergotende, vetrate)
  const reducedServices = [
    {
      name: "Pergole Bioclimatiche",
      description: `Pergole bioclimatiche con lamelle orientabili a ${location.content?.areas?.[0] || 'nella zona'}. Strutture in alluminio certificate, controllo domotico.`,
      link: "/bioclimatiche",
      features: ["Lamelle orientabili 0°-150°", "Alluminio certificato", "Sensori climatici", "App smartphone"]
    },
    {
      name: "Pergotende",
      description: "Pergotende su misura per terrazzi e giardini. Protezione da sole, pioggia e vento tutto l'anno.",
      link: "/pergotende",
      features: ["Teli impermeabili", "Strutture autoportanti", "Design personalizzato", "Motorizzazione opzionale"]
    },
    {
      name: "Vetrate Panoramiche",
      description: "Chiusure vetrate scorrevoli per pergole e verande. Vetri temperati, isolamento termico.",
      link: "/vetrate-panoramiche",
      features: ["Vetri temperati", "Sistemi scorrevoli", "Isolamento termico", "Apertura totale"]
    }
  ];

  // Scegli i servizi in base alla città
  const services = isBioclimaticheOnly ? reducedServices : allServices;

  const locationNames = {
    pisa: "Pisa",
    provinciaPisa: "Provincia di Pisa",
    lucca: "Lucca",
    livorno: "Livorno",
    firenze: "Firenze",
    massaCarrara: "Massa-Carrara",
    pistoia: "Pistoia"
  };

  const heroImages = {
    pisa: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1920&q=80",
    provinciaPisa: "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=1920&q=80",
    lucca: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1920&q=80",
    livorno: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1920&q=80",
    firenze: "https://images.unsplash.com/photo-1600607687644-c7171b42498f?w=1920&q=80",
    massaCarrara: "https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=1920&q=80",
    pistoia: "https://images.unsplash.com/photo-1600566753151-384129cf4e3e?w=1920&q=80"
  };

  // Video hero per le pagine delle città
  const heroVideoUrl = "https://customer-assets.emergentagent.com/job_828fb547-63e8-4146-aaa2-2aed84a5e0d5/artifacts/zq7ral91_hf_20260217_173729_8541910f-1fd2-4de2-b3e9-7cfe92715649.mp4";

  return (
    <Layout>
      <SEOHead
        title={location.title}
        description={location.description}
        keywords={location.keywords}
        schema={generateBreadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Aree Servite', path: '/aree-servite' },
          { name: locationNames[locationKey], path: `/aree-servite/${locationId}` }
        ])}
      />

      {/* Hero Section with Video */}
      <HeroSection
        title={location.h1}
        subtitle={`Installazioni professionali di pergole bioclimatiche, pergotende e tende da sole a ${locationNames[locationKey]} e dintorni`}
        ctaText="Richiedi preventivo gratuito"
        ctaLink="/preventivo"
        videoUrl={heroVideoUrl}
        imageUrl={heroImages[locationKey]}
        showScrollIndicator={true}
        minHeight="70vh"
      />

      {/* Intro Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <FadeInOnScroll>
              <div>
                <span className="inline-block text-sm font-medium tracking-widest uppercase mb-4 text-sky-500">
                  {locationNames[locationKey]}
                </span>
                <h2 className="text-3xl md:text-4xl font-light text-gray-900 leading-tight mb-6">
                  Soluzioni Outdoor di Prestigio
                </h2>
                <div className="prose prose-lg text-gray-600 font-light leading-relaxed">
                  <p>{location.content?.intro}</p>
                  {location.content?.climate && <p>{location.content.climate}</p>}
                </div>
                {/* Bottone sopralluogo solo per Pisa e Provincia */}
                {!isBioclimaticheOnly && (
                  <div className="mt-8">
                    <Link 
                      to="/preventivo"
                      className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-600 text-white px-6 py-3 transition-colors"
                    >
                      Richiedi sopralluogo gratuito
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                )}
              </div>
            </FadeInOnScroll>
            <FadeInOnScroll delay={200}>
              <div className="bg-gray-50 p-8">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-sky-500" />
                  Zone Coperte
                </h3>
                <div className="flex flex-wrap gap-2">
                  {(location.content?.areas || location.content?.towns || []).map((area, index) => (
                    <span 
                      key={index}
                      className="bg-white border border-gray-200 text-gray-700 px-3 py-1.5 text-sm"
                    >
                      {area}
                    </span>
                  ))}
                </div>
              </div>
            </FadeInOnScroll>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 lg:py-28 bg-gray-50">
        <div className="container mx-auto px-6 lg:px-12">
          <FadeInOnScroll>
            <SectionTitle
              label="I Nostri Servizi"
              title={`Cosa Installiamo a ${locationNames[locationKey]}`}
              description="Offriamo la gamma completa di soluzioni outdoor, dalla progettazione all'installazione."
            />
          </FadeInOnScroll>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <FadeInOnScroll key={index} delay={index * 100}>
                <div className="bg-white p-8 border border-gray-100 hover:border-sky-200 transition-all duration-300 hover:shadow-lg group">
                  <h3 className="text-xl font-medium text-gray-900 mb-3 group-hover:text-sky-500 transition-colors">
                    {service.name}
                  </h3>
                  <p className="text-gray-600 font-light mb-4">
                    {service.description}
                  </p>
                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-center gap-2 text-sm text-gray-600">
                        <Check className="w-4 h-4 text-sky-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Link 
                    to={service.link}
                    className="inline-flex items-center text-sky-500 text-sm font-medium hover:gap-3 gap-2 transition-all"
                  >
                    Scopri di più
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </FadeInOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* Photo Gallery - I Nostri Lavori */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <FadeInOnScroll>
            <SectionTitle
              label="I Nostri Lavori"
              title="Installazioni in Toscana e oltre"
              description="Ecco alcune delle nostre realizzazioni più recenti."
            />
          </FadeInOnScroll>

          {/* Collage Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 max-w-5xl mx-auto">
            {/* Toscana Notte */}
            <FadeInOnScroll className="col-span-2 row-span-2">
              <div className="relative overflow-hidden rounded-lg shadow-lg group h-full">
                <img
                  src="https://customer-assets.emergentagent.com/job_tuscany-tende/artifacts/19exinuu_biocliamtica%20toscana%20notte.jpeg"
                  alt="Bioclimatica illuminata di notte in Toscana"
                  className="w-full h-full min-h-[280px] object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            </FadeInOnScroll>

            {/* Azienda Vinicola */}
            <FadeInOnScroll delay={100}>
              <div className="relative overflow-hidden rounded-lg shadow-lg group">
                <img
                  src="https://customer-assets.emergentagent.com/job_tuscany-tende/artifacts/cnsdws3c_bioclima%20azienda%20vinicola.png"
                  alt="Bioclimatica presso azienda vinicola toscana"
                  className="w-full h-32 lg:h-36 object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            </FadeInOnScroll>

            {/* Sardegna Tavolara */}
            <FadeInOnScroll delay={150}>
              <div className="relative overflow-hidden rounded-lg shadow-lg group">
                <img
                  src="https://customer-assets.emergentagent.com/job_tuscany-tende/artifacts/hzonqwij_bioclima%20sardegna%20tavolara.png"
                  alt="Bioclimatica in Sardegna con vista Tavolara"
                  className="w-full h-32 lg:h-36 object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            </FadeInOnScroll>

            {/* Multishot Sardegna */}
            <FadeInOnScroll delay={200} className="col-span-2">
              <div className="relative overflow-hidden rounded-lg shadow-lg group">
                <img
                  src="https://customer-assets.emergentagent.com/job_tuscany-tende/artifacts/sern1x99_multishot%20saredagna.png"
                  alt="Bioclimatica in Sardegna - Collage"
                  className="w-full h-32 lg:h-36 object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            </FadeInOnScroll>
          </div>

          <FadeInOnScroll delay={250}>
            <div className="text-center mt-8">
              <Link 
                to="/installazioni"
                className="inline-flex items-center gap-2 text-sky-500 font-medium hover:gap-3 transition-all"
              >
                Vedi tutte le installazioni
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </FadeInOnScroll>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <FadeInOnScroll>
            <SectionTitle
              label="Perché Noi"
              title={`Perché Scegliere Tappezzeria Giovanni a ${locationNames[locationKey]}`}
            />
          </FadeInOnScroll>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Esperienza Locale",
                description: `Oltre 60 anni di installazioni in Toscana. Conosciamo il territorio, il clima e le esigenze architettoniche di ${locationNames[locationKey]}.`
              },
              {
                title: "Qualità Premium",
                description: "Utilizziamo solo materiali di prima scelta e lavoriamo con i migliori brand del settore outdoor."
              },
              {
                title: "Assistenza Completa",
                description: "Dalla consulenza iniziale all'assistenza post-vendita, ti seguiamo in ogni fase del progetto."
              }
            ].map((item, index) => (
              <FadeInOnScroll key={index} delay={index * 100}>
                <div className="text-center p-6">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-sky-500/10 flex items-center justify-center">
                    <span className="text-sky-500 font-semibold">{index + 1}</span>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 font-light text-sm">{item.description}</p>
                </div>
              </FadeInOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* Parallax Section - Solo per Pisa e Provincia */}
      {!isBioclimaticheOnly && (
        <ParallaxSection
          imageUrl="https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?w=1920&q=80"
          speed={0.3}
          overlayOpacity={0.5}
          minHeight="50vh"
        >
          <div className="h-[50vh] flex items-center justify-center">
            <div className="text-center px-6">
              <ParallaxElement speed={0.1} direction="up">
                <h2 className="text-3xl md:text-4xl font-light text-white mb-4">
                  Richiedi un Sopralluogo Gratuito
                </h2>
                <p className="text-lg text-white/80 font-light mb-6 max-w-xl mx-auto">
                  Un nostro tecnico verrà a valutare il tuo spazio e proporre la soluzione ideale.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <a 
                    href={`tel:+39${companyInfo.phone.replace(/\s/g, '')}`}
                    className="flex items-center gap-2 bg-white text-gray-900 px-6 py-3 hover:bg-gray-100 transition-colors"
                  >
                    <Phone className="w-4 h-4" />
                    Chiama ora: {companyInfo.phone}
                  </a>
                  <Link 
                    to="/preventivo"
                    className="flex items-center gap-2 bg-sky-500 text-white px-6 py-3 hover:bg-sky-600 transition-colors"
                  >
                    Preventivo online
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </ParallaxElement>
            </div>
          </div>
        </ParallaxSection>
      )}

      {/* CTA Section */}
      <CTASection
        title={`Trasforma il Tuo Spazio a ${locationNames[locationKey]}`}
        description="Contattaci per una consulenza gratuita e senza impegno."
        variant="light"
      />
    </Layout>
  );
};

export default LocationPage;
